"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

type Pharmacy = "Square" | "Ragadan" | "Laurentine";
type Strategy = "patient" | "balanced" | "margin";
type DecisionStatus = "to_decide" | "ready" | "applied" | "returned";
type PromoLabelFormat = "label40" | "label50" | "label60" | "label70" | "label80" | "label100";
type PromoLabelStyle = "fresh" | "impact" | "blue" | "beauty";

type NiftyRow = {
  pharmacy: string;
  ean: string;
  product: string;
  supplier: string;
  stock: number;
  monthly_sales: number;
  purchase_price: number;
  sale_price: number;
  vat_rate: number;
  baseline_price: number;
  proposed_price: number;
  return_price: number;
  status: DecisionStatus;
  note: string;
  campaign: {
    id: string;
    title: string;
    laboratory: string;
    discountKind: "amount" | "percent" | "second_item_percent";
    discountValue: number;
    startDate: string;
    endDate: string;
  };
};

type NiftyHistoryChange = {
  action?: "saved" | "deleted";
  ean: string;
  product: string;
  campaignId: string;
  campaignTitle: string;
  laboratory: string;
  campaignStartDate?: string;
  campaignEndDate?: string;
  previousPrice: number;
  proposedPrice: number;
  netPatient: number;
  returnPrice: number;
  previousStatus: DecisionStatus;
  status: DecisionStatus;
  priceChanged: boolean;
};

type NiftyHistoryEntry = {
  id: string | number;
  pharmacy: string;
  createdAt: number;
  changes: NiftyHistoryChange[];
};

type NiftyHistoryCampaign = {
  key: string;
  campaignId: string;
  campaignTitle: string;
  laboratory: string;
  startDate: string;
  endDate: string;
  updatedAt: number;
  changes: Array<NiftyHistoryChange & { recordedAt: number }>;
};

const pharmacies: Pharmacy[] = ["Square", "Ragadan", "Laurentine"];
const promoFormats: Array<{ value: PromoLabelFormat; label: string; size: string }> = [
  { value: "label40", label: "Petite", size: "40 × 30 mm" },
  { value: "label50", label: "Compacte", size: "50 × 30 mm" },
  { value: "label60", label: "Standard", size: "60 × 40 mm" },
  { value: "label70", label: "Rayon", size: "70 × 37 mm" },
  { value: "label80", label: "Grande", size: "80 × 60 mm" },
  { value: "label100", label: "Affiche", size: "100 × 70 mm" },
];
const promoStyles: Array<{ value: PromoLabelStyle; label: string }> = [
  { value: "fresh", label: "Pharma fraîche" },
  { value: "impact", label: "Promo vive" },
  { value: "blue", label: "Bleu confiance" },
  { value: "beauty", label: "Beauté douce" },
];
const strategyPatientShare: Record<Strategy, number> = { patient: .75, balanced: .5, margin: .35 };
const money = (value: number) => value.toLocaleString("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });
const numberValue = (value: unknown) => {
  const parsed = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};
const roundRetail = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const step = value < 10 ? .05 : value < 30 ? .1 : .5;
  const rounded = Math.round(value / step) * step;
  return Math.max(.01, Math.round((rounded - .01) * 100) / 100);
};
const dateLabel = (value: string) => value ? new Date(`${value}T12:00:00`).toLocaleDateString("fr-FR") : "À préciser";
const daysUntil = (value: string) => value ? Math.ceil((new Date(`${value}T23:59:59`).getTime() - Date.now()) / 86_400_000) : 9999;
const dayAfter = (value: string) => {
  if (!value) return "";
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};
const niftyTodoRef = (phase: "start" | "return", pharmacy: string, campaignId: string) => `nifty-${phase}:${pharmacy}:${campaignId}`;
function discountLabel(row: NiftyRow) {
  if (row.campaign.discountKind === "amount") return `-${money(row.campaign.discountValue)}`;
  if (row.campaign.discountKind === "second_item_percent") return `-${row.campaign.discountValue}% sur la 2e`;
  return `-${row.campaign.discountValue}%`;
}

function patientNet(row: NiftyRow, grossPrice: number) {
  if (row.campaign.discountKind === "amount") return Math.max(0, grossPrice - row.campaign.discountValue);
  const rate = row.campaign.discountKind === "second_item_percent"
    ? row.campaign.discountValue / 200
    : row.campaign.discountValue / 100;
  return grossPrice * (1 - rate);
}

function recommendedPrice(row: NiftyRow, strategy: Strategy) {
  const baseline = numberValue(row.baseline_price) || numberValue(row.sale_price);
  if (!baseline || !row.campaign.discountValue) return baseline;
  const patientShare = strategyPatientShare[strategy];
  let recommendation = baseline;
  if (row.campaign.discountKind === "amount") {
    recommendation = baseline + row.campaign.discountValue * (1 - patientShare);
  } else {
    const rate = row.campaign.discountKind === "second_item_percent"
      ? row.campaign.discountValue / 200
      : row.campaign.discountValue / 100;
    const targetPatientPrice = baseline * (1 - rate * patientShare);
    recommendation = targetPatientPrice / Math.max(.01, 1 - rate);
  }
  return roundRetail(Math.min(recommendation, baseline * 1.15));
}

function economics(row: NiftyRow) {
  const baseline = numberValue(row.baseline_price) || numberValue(row.sale_price);
  const proposed = numberValue(row.proposed_price) || baseline;
  const vat = numberValue(row.vat_rate) || 20;
  const purchase = numberValue(row.purchase_price);
  const netPatient = patientNet(row, proposed);
  const patientSaving = proposed - netPatient;
  const marginGain = (proposed - baseline) / (1 + vat / 100);
  const proposedHt = proposed / (1 + vat / 100);
  const markRate = proposedHt > 0 && purchase > 0 ? (proposedHt - purchase) / proposedHt * 100 : 0;
  return { baseline, proposed, netPatient, patientSaving, marginGain, markRate, stockGain: marginGain * numberValue(row.stock), monthlyGain: marginGain * numberValue(row.monthly_sales) };
}

export function NiftyPricePilot({ pharmacy, onPharmacyChange }: { pharmacy: Pharmacy; onPharmacyChange: (value: Pharmacy) => void }) {
  const [rows, setRows] = useState<NiftyRow[]>([]);
  const savedRowsRef = useRef<Map<string, NiftyRow>>(new Map());
  const [history, setHistory] = useState<NiftyHistoryEntry[]>([]);
  const [strategy, setStrategy] = useState<Strategy>("balanced");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"potential" | "end" | "product" | "supplier" | "decision">("potential");
  const [filter, setFilter] = useState<"all" | "to_decide" | "decided" | "ready" | "applied" | "returned" | "urgent" | "expired">("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [selectedEans, setSelectedEans] = useState<string[]>([]);
  const [promoDialog, setPromoDialog] = useState(false);
  const [promoFormat, setPromoFormat] = useState<PromoLabelFormat>("label60");
  const [promoStyle, setPromoStyle] = useState<PromoLabelStyle>("fresh");
  const [promoCopies, setPromoCopies] = useState(1);
  const [sourceStats, setSourceStats] = useState({ campaignCount: 0, eligibleEanCount: 0, matchedEanCount: 0 });
  const todoSyncedFor = useRef("");

  useEffect(() => {
    setLoading(true);
    setGroupFilter("all");
    setSelectedEans([]);
    void fetch(`/api/nifty-pricing?pharmacy=${encodeURIComponent(pharmacy)}`, { cache: "no-store" })
      .then(async (response) => {
        const payload = await response.json() as { items?: NiftyRow[]; campaignCount?: number; eligibleEanCount?: number; matchedEanCount?: number; error?: string };
        if (!response.ok) throw new Error(payload.error || "Pilotage Nifty indisponible.");
        const items = (payload.items ?? []).map((row) => ({
          ...row,
          proposed_price: numberValue(row.proposed_price) || recommendedPrice(row, strategy),
          return_price: numberValue(row.return_price) || numberValue(row.baseline_price) || numberValue(row.sale_price),
        }));
        setRows(items);
        savedRowsRef.current = new Map(items.map((row) => [row.ean, { ...row, campaign: { ...row.campaign } }]));
        setSourceStats({ campaignCount: payload.campaignCount ?? 0, eligibleEanCount: payload.eligibleEanCount ?? 0, matchedEanCount: payload.matchedEanCount ?? items.length });
      })
      .catch((error) => setNotice(error instanceof Error ? error.message : "Pilotage Nifty indisponible."))
      .finally(() => setLoading(false));
  }, [pharmacy]);

  useEffect(() => {
    void fetch("/api/nifty-history?limit=100", { cache: "no-store" })
      .then(async (response) => {
        const payload = await response.json() as { items?: NiftyHistoryEntry[]; error?: string };
        if (!response.ok) throw new Error(payload.error || "Historique Nifty indisponible.");
        setHistory((payload.items ?? [])
          .filter((entry) => entry.pharmacy === pharmacy && Array.isArray(entry.changes))
          .sort((left, right) => numberValue(right.createdAt) - numberValue(left.createdAt))
          .slice(0, 50));
      })
      .catch(() => setHistory([]));
  }, [pharmacy]);

  const recalculate = (nextStrategy: Strategy) => {
    setStrategy(nextStrategy);
    setRows((current) => current.map((row) => ({ ...row, proposed_price: recommendedPrice(row, nextStrategy) })));
  };

  const updateRow = (ean: string, patch: Partial<NiftyRow>) => setRows((current) => current.map((row) => row.ean === ean ? { ...row, ...patch } : row));

  const campaignGroups = useMemo(() => {
    const groups = new Map<string, { key: string; laboratory: string; endDate: string; families: Set<string>; count: number; rows: NiftyRow[] }>();
    for (const row of rows) {
      const laboratory = row.campaign.laboratory?.trim() || row.supplier.trim() || "Laboratoire à qualifier";
      const endDate = row.campaign.endDate || "";
      const key = `${laboratory}::${endDate || "sans-date"}`;
      const current = groups.get(key) ?? { key, laboratory, endDate, families: new Set<string>(), count: 0, rows: [] };
      current.families.add(row.campaign.title);
      current.count += 1;
      current.rows.push(row);
      groups.set(key, current);
    }
    return [...groups.values()].sort((left, right) => daysUntil(left.endDate) - daysUntil(right.endDate) || left.laboratory.localeCompare(right.laboratory, "fr"));
  }, [rows]);

  const displayedRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("fr");
    const decisionOrder: Record<DecisionStatus, number> = { to_decide: 0, ready: 1, applied: 2, returned: 3 };
    return rows.filter((row) => !query || `${row.product} ${row.ean} ${row.campaign.title} ${row.campaign.laboratory} ${row.supplier}`.toLocaleLowerCase("fr").includes(query))
      .filter((row) => groupFilter === "all" || `${row.campaign.laboratory?.trim() || row.supplier.trim() || "Laboratoire à qualifier"}::${row.campaign.endDate || "sans-date"}` === groupFilter)
      .filter((row) => {
        if (filter === "all") return true;
        if (filter === "decided") return row.status !== "to_decide";
        if (filter === "urgent") return daysUntil(row.campaign.endDate) >= 0 && daysUntil(row.campaign.endDate) <= 7;
        if (filter === "expired") return daysUntil(row.campaign.endDate) < 0 && row.status !== "returned";
        return row.status === filter;
      })
      .sort((left, right) => {
        if (sort === "end") return daysUntil(left.campaign.endDate) - daysUntil(right.campaign.endDate) || left.product.localeCompare(right.product, "fr");
        if (sort === "product") return left.product.localeCompare(right.product, "fr");
        if (sort === "supplier") return (left.campaign.laboratory || left.supplier || "Laboratoire non renseigné").localeCompare(right.campaign.laboratory || right.supplier || "Laboratoire non renseigné", "fr") || left.product.localeCompare(right.product, "fr");
        if (sort === "decision") return decisionOrder[left.status] - decisionOrder[right.status] || left.product.localeCompare(right.product, "fr");
        return economics(right).stockGain - economics(left).stockGain || economics(right).monthlyGain - economics(left).monthlyGain;
      });
  }, [filter, groupFilter, rows, search, sort]);

  const summary = useMemo(() => rows.reduce((totals, row) => {
    const value = economics(row);
    totals.marginGain += Math.max(0, value.stockGain);
    totals.patientSaving += Math.max(0, value.patientSaving) * Math.max(1, numberValue(row.stock));
    totals.toDecide += row.status === "to_decide" ? 1 : 0;
    totals.urgent += daysUntil(row.campaign.endDate) >= 0 && daysUntil(row.campaign.endDate) <= 7 ? 1 : 0;
    totals.expired += daysUntil(row.campaign.endDate) < 0 && row.status !== "returned" ? 1 : 0;
    return totals;
  }, { marginGain: 0, patientSaving: 0, toDecide: 0, urgent: 0, expired: 0 }), [rows]);

  const selectedRows = useMemo(() => rows.filter((row) => selectedEans.includes(row.ean)), [rows, selectedEans]);
  const displayedDecisionRows = useMemo(() => displayedRows.filter((row) => row.status !== "to_decide"), [displayedRows]);
  const historyCampaigns = useMemo(() => {
    const groups = new Map<string, NiftyHistoryCampaign & { seen: Set<string> }>();
    for (const entry of [...history].sort((left, right) => right.createdAt - left.createdAt)) {
      for (const change of entry.changes) {
        const currentRow = rows.find((row) => row.ean === change.ean && (!change.campaignId || row.campaign.id === change.campaignId));
        const campaignId = change.campaignId || currentRow?.campaign.id || change.campaignTitle || change.laboratory || "campagne";
        const startDate = change.campaignStartDate || currentRow?.campaign.startDate || "";
        const endDate = change.campaignEndDate || currentRow?.campaign.endDate || "";
        const key = `${campaignId}|${startDate}|${endDate}`;
        const group = groups.get(key) ?? {
          key,
          campaignId,
          campaignTitle: change.campaignTitle || currentRow?.campaign.title || "Campagne Nifty",
          laboratory: change.laboratory || currentRow?.campaign.laboratory || currentRow?.supplier || "Nifty",
          startDate,
          endDate,
          updatedAt: entry.createdAt,
          changes: [],
          seen: new Set<string>(),
        };
        group.updatedAt = Math.max(group.updatedAt, entry.createdAt);
        if (group.seen.has(change.ean)) continue;
        group.seen.add(change.ean);
        group.changes.push({ ...change, campaignStartDate: startDate, campaignEndDate: endDate, recordedAt: entry.createdAt });
        groups.set(key, group);
      }
    }
    return [...groups.values()]
      .sort((left, right) => right.updatedAt - left.updatedAt)
      .map(({ seen: _seen, ...group }) => group);
  }, [history, rows]);
  const allDisplayedSelected = displayedRows.length > 0 && displayedRows.every((row) => selectedEans.includes(row.ean));
  const toggleSelection = (ean: string) => setSelectedEans((current) => current.includes(ean) ? current.filter((item) => item !== ean) : [...current, ean]);
  const toggleDisplayed = () => setSelectedEans((current) => {
    const displayed = new Set(displayedRows.map((row) => row.ean));
    return allDisplayedSelected ? current.filter((ean) => !displayed.has(ean)) : [...new Set([...current, ...displayed])];
  });
  const printPromoLabels = () => {
    setPromoDialog(false);
    document.body.classList.add("nifty-label-printing");
    window.addEventListener("afterprint", () => document.body.classList.remove("nifty-label-printing"), { once: true });
    window.setTimeout(() => window.print(), 120);
  };

  const syncNiftyTodos = async (allRows: NiftyRow[]) => {
    const tracked = allRows.filter((row) => ["ready", "applied"].includes(row.status) && (row.campaign.startDate || row.campaign.endDate));
    const taskResponse = await fetch("/api/daily-tasks", { cache: "no-store" });
    const taskPayload = await taskResponse.json() as { tasks?: Array<Record<string, unknown>>; error?: string };
    if (!taskResponse.ok) throw new Error(taskPayload.error || "Lecture de la To-do impossible.");
    const existing = taskPayload.tasks ?? [];
    const groups = new Map<string, NiftyRow[]>();
    for (const row of tracked) {
      const key = `${row.campaign.id}::${row.campaign.endDate}`;
      groups.set(key, [...(groups.get(key) ?? []), row]);
    }
    const desiredTasks = new Map<string, Record<string, unknown>>();
    for (const group of groups.values()) {
      const campaign = group[0].campaign;
      const startRows = group.filter((row) => row.status === "ready");
      const returnRows = group.filter((row) => row.status === "applied");
      const setupLines = startRows.map((row) => {
        const value = economics(row);
        return `${row.product || row.ean} · EAN ${row.ean} · passer de ${money(value.baseline)} à ${money(value.proposed)} dans LGPI · patient ${money(value.netPatient)} après Nifty`;
      });
      const returnLines = returnRows.map((row) => {
        const value = economics(row);
        return `${row.product || row.ean} · EAN ${row.ean} · remettre ${money(row.return_price || value.baseline)} (prix promo affiché ${money(value.proposed)}, net Nifty ${money(value.netPatient)})`;
      });
      const tasks = [
        ...(startRows.length && campaign.startDate ? [{
          phase: "start" as const,
          title: `Mettre en place la période Nifty · ${campaign.laboratory || campaign.title}`,
          details: `La période « ${campaign.title} » démarre. Modifier les prix dans LGPI, imprimer puis poser les affiches Nifty :\n${setupLines.join("\n")}`,
          priority: "important",
          displayDate: campaign.startDate,
          dueTime: "08:30",
        }] : []),
        ...(returnRows.length && campaign.endDate ? [{
          phase: "return" as const,
          title: `Remettre les prix Nifty · ${campaign.laboratory || campaign.title}`,
          details: `La période « ${campaign.title} » est terminée. Remettre dans LGPI les prix mémorisés :\n${returnLines.join("\n")}`,
          priority: "urgent",
          displayDate: dayAfter(campaign.endDate),
          dueTime: "09:00",
        }] : []),
      ];
      for (const reminder of tasks) {
        const sourceRef = niftyTodoRef(reminder.phase, pharmacy, campaign.id);
        desiredTasks.set(sourceRef, {
          kind: "task",
          title: reminder.title,
          details: reminder.details,
          priority: reminder.priority,
          pharmacies: [pharmacy],
          createdBy: "Pilotage prix Nifty",
          assignedTo: "",
          displayDate: reminder.displayDate,
          dueTime: reminder.dueTime,
          recipient: "",
          phone: "",
          address: "",
          sourceRef,
        });
      }
    }
    let synced = 0;
    for (const [sourceRef, desired] of desiredTasks) {
        const current = existing.find((task) => String(task.sourceRef ?? task.source_ref ?? "") === sourceRef);
        const task = { ...desired, id: current?.id, status: String(current?.status || "open") };
        const unchanged = current
          && String(current.title || "") === String(task.title || "")
          && String(current.details || "") === String(task.details || "")
          && String(current.displayDate ?? current.display_date ?? "") === String(task.displayDate || "")
          && String(current.dueTime ?? current.due_time ?? "") === String(task.dueTime || "");
        if (unchanged) continue;
        const response = await fetch("/api/daily-tasks", {
          method: current ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(task),
        });
        const payload = await response.json() as { error?: string };
        if (!response.ok) throw new Error(payload.error || "Création du rappel Nifty impossible.");
        synced += 1;
    }
    const desiredRefs = new Set(desiredTasks.keys());
    const obsolete = existing.filter((task) => {
      const sourceRef = String(task.sourceRef ?? task.source_ref ?? "");
      return sourceRef.startsWith(`nifty-`) && sourceRef.includes(`:${pharmacy}:`) && !desiredRefs.has(sourceRef) && String(task.status || "open") === "open";
    });
    for (const task of obsolete) {
      const response = await fetch(`/api/daily-tasks?id=${encodeURIComponent(String(task.id || ""))}`, { method: "DELETE" });
      if (!response.ok) throw new Error("Suppression d’un ancien rappel Nifty impossible.");
      synced += 1;
    }
    return synced;
  };

  useEffect(() => {
    if (loading || todoSyncedFor.current === pharmacy) return;
    todoSyncedFor.current = pharmacy;
    void syncNiftyTodos(rows).catch((error) => setNotice(error instanceof Error ? error.message : "Synchronisation des alertes Nifty impossible."));
  }, [loading, pharmacy]);

  const save = async () => {
    if (!selectedRows.length) {
      setNotice("Cochez au moins un produit à enregistrer.");
      return;
    }
    const decisionRows = selectedRows;
    setSaving(true);
    try {
      const response = await fetch("/api/nifty-pricing", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pharmacy,
          items: decisionRows.map((row) => ({
            ean: row.ean,
            baselinePrice: row.baseline_price || row.sale_price,
            proposedPrice: row.proposed_price,
            returnPrice: row.return_price,
            status: row.status,
            note: row.note,
          })),
        }),
      });
      const payload = await response.json() as { saved?: number; error?: string };
      if (!response.ok) throw new Error(payload.error || "Enregistrement impossible.");
      const changes = decisionRows.flatMap((row): NiftyHistoryChange[] => {
        const previous = savedRowsRef.current.get(row.ean);
        const value = economics(row);
        const previousPrice = previous?.status === "applied"
          ? numberValue(previous.proposed_price) || value.baseline
          : numberValue(previous?.baseline_price) || value.baseline;
        const changed = !previous
          || previousPrice !== value.proposed
          || numberValue(previous?.return_price) !== numberValue(row.return_price)
          || previous?.status !== row.status;
        return changed ? [{
          action: "saved",
          ean: row.ean,
          product: row.product || row.campaign.title,
          campaignId: row.campaign.id,
          campaignTitle: row.campaign.title,
          laboratory: row.campaign.laboratory || row.supplier,
          campaignStartDate: row.campaign.startDate,
          campaignEndDate: row.campaign.endDate,
          previousPrice,
          proposedPrice: value.proposed,
          netPatient: value.netPatient,
          returnPrice: numberValue(row.return_price) || value.baseline,
          previousStatus: previous?.status ?? "to_decide",
          status: row.status,
          priceChanged: row.status === "applied",
        }] : [];
      });
      if (changes.length) {
        const historyEntry = { pharmacy, kind: "nifty-price-history", createdAt: Date.now(), changes };
        const historyResponse = await fetch("/api/nifty-history", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(historyEntry),
        });
        const savedHistory = await historyResponse.json() as Partial<NiftyHistoryEntry> & { error?: string };
        if (!historyResponse.ok) throw new Error(savedHistory.error || "Prix enregistrés, mais sauvegarde de l’historique Nifty impossible.");
        setHistory((current) => [{ ...historyEntry, id: savedHistory.id ?? `local-${historyEntry.createdAt}` }, ...current].slice(0, 50));
      }
      for (const row of decisionRows) savedRowsRef.current.set(row.ean, { ...row, campaign: { ...row.campaign } });
      const reminders = await syncNiftyTodos(rows);
      setSelectedEans([]);
      setNotice(`${payload.saved ?? decisionRows.length} décision(s) Nifty sélectionnée(s) enregistrée(s) pour ${pharmacy}.${changes.length ? ` ${changes.length} modification(s) ajoutée(s) à l’historique.` : " Aucune nouvelle modification."}${reminders ? ` ${reminders} rappel(s) de début et fin de période synchronisé(s) avec la To-do.` : ""}`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const deleteDecisions = async (decisionRows: NiftyRow[]) => {
    if (!decisionRows.length) {
      setNotice("Aucune décision enregistrée à supprimer dans cet affichage.");
      return;
    }
    const singleLabel = decisionRows.length === 1 ? ` « ${decisionRows[0].product || decisionRows[0].ean} »` : "";
    if (!window.confirm(`Supprimer ${decisionRows.length} décision(s) Nifty${singleLabel} pour ${pharmacy} ? L’historique restera consultable.`)) return;
    setSaving(true);
    try {
      const response = await fetch("/api/nifty-pricing", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pharmacy,
          items: decisionRows.map((row) => ({
            ean: row.ean,
            baselinePrice: 0,
            proposedPrice: 0,
            returnPrice: 0,
            status: "to_decide",
            note: "",
          })),
        }),
      });
      const payload = await response.json() as { saved?: number; error?: string };
      if (!response.ok) throw new Error(payload.error || "Suppression impossible.");
      const changes: NiftyHistoryChange[] = decisionRows.map((row) => {
        const value = economics(row);
        return {
          action: "deleted",
          ean: row.ean,
          product: row.product || row.campaign.title,
          campaignId: row.campaign.id,
          campaignTitle: row.campaign.title,
          laboratory: row.campaign.laboratory || row.supplier,
          campaignStartDate: row.campaign.startDate,
          campaignEndDate: row.campaign.endDate,
          previousPrice: value.proposed,
          proposedPrice: value.baseline,
          netPatient: patientNet(row, value.baseline),
          returnPrice: value.baseline,
          previousStatus: row.status,
          status: "to_decide",
          priceChanged: false,
        };
      });
      const historyEntry = { pharmacy, kind: "nifty-price-history", createdAt: Date.now(), changes };
      const historyResponse = await fetch("/api/nifty-history", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(historyEntry),
      });
      const savedHistory = await historyResponse.json() as Partial<NiftyHistoryEntry> & { error?: string };
      if (!historyResponse.ok) throw new Error(savedHistory.error || "Décisions supprimées, mais traçabilité impossible.");
      setHistory((current) => [{ ...historyEntry, id: savedHistory.id ?? `local-${historyEntry.createdAt}` }, ...current].slice(0, 50));
      const deleted = new Set(decisionRows.map((row) => row.ean));
      const nextRows: NiftyRow[] = rows.map((row) => deleted.has(row.ean) ? {
        ...row,
        proposed_price: recommendedPrice(row, strategy),
        return_price: numberValue(row.baseline_price) || numberValue(row.sale_price),
        status: "to_decide",
        note: "",
      } : row);
      setRows(nextRows);
      await syncNiftyTodos(nextRows);
      for (const row of decisionRows) savedRowsRef.current.delete(row.ean);
      setSelectedEans((current) => current.filter((ean) => !deleted.has(ean)));
      setNotice(`${payload.saved ?? decisionRows.length} décision(s) supprimée(s). L’historique a été conservé.`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Suppression impossible.");
    } finally {
      setSaving(false);
    }
  };

  const editHistoryDecision = (change: NiftyHistoryChange) => {
    const row = rows.find((item) => item.ean === change.ean);
    if (!row) {
      setNotice("Ce produit n’est plus présent dans le catalogue actuel de cette pharmacie.");
      return;
    }
    setGroupFilter("all");
    setFilter("all");
    setSearch(change.ean);
    updateRow(change.ean, {
      proposed_price: change.proposedPrice,
      return_price: change.returnPrice,
      status: change.status,
    });
    setSelectedEans((current) => current.includes(change.ean) ? current : [...current, change.ean]);
    setNotice("Décision chargée dans le tableau : modifiez-la puis cliquez sur « Enregistrer la sélection ».");
    window.setTimeout(() => document.getElementById(`nifty-row-${change.ean}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 80);
  };

  const deleteHistoryDecision = (change: NiftyHistoryChange) => {
    const row = rows.find((item) => item.ean === change.ean);
    if (!row || row.status === "to_decide") {
      setNotice("Cette décision est déjà supprimée ou n’est plus active.");
      return;
    }
    void deleteDecisions([row]);
  };

  const exportCsv = () => {
    const cells = (values: unknown[]) => values.map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`).join(";");
    const lines = [cells(["Laboratoire", "EAN", "Produit", "Campagne", "Fin", "Prix LGPI actuel", "Nouveau prix TTC", "Prix net patient", "Prix retour après promo", "Gain marge HT/unité", "Statut"])];
    for (const row of displayedRows) {
      const value = economics(row);
      lines.push(cells([row.campaign.laboratory || row.supplier, row.ean, row.product, row.campaign.title, row.campaign.endDate, value.baseline.toFixed(2), value.proposed.toFixed(2), value.netPatient.toFixed(2), row.return_price.toFixed(2), value.marginGain.toFixed(2), row.status]));
    }
    const url = URL.createObjectURL(new Blob([`\uFEFF${lines.join("\n")}`], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `nifty-prix-${pharmacy.toLocaleLowerCase("fr")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return <><section className="nifty-pilot">
    <header className="nifty-pilot-hero">
      <div><span>★ NIFTY · PILOTAGE DE MARGE</span><h2>Transformer la promo en avantage patient et en marge</h2><p>Les EAN Nifty sont croisés avec les prix, achats et stocks LGPI. Vous décidez le nouveau prix, le prix net patient et le prix de retour après la campagne.</p></div>
      <aside><small>OPPORTUNITÉ SUR LE STOCK</small><strong>{money(summary.marginGain)}</strong><span>de marge HT supplémentaire estimée</span></aside>
    </header>

    <div className="nifty-pilot-toolbar">
      <div className="nifty-pharmacies">{pharmacies.map((item) => <button type="button" className={pharmacy === item ? "active" : ""} onClick={() => onPharmacyChange(item)} key={item}>{pharmacy === item ? "✓ " : ""}{item}</button>)}</div>
      <div className="nifty-strategies"><span>Part de la promo laissée au patient</span>{([[
        "patient", "Patient +", "75%"], ["balanced", "Équilibré", "50%"], ["margin", "Marge +", "35%"]] as Array<[Strategy, string, string]>).map(([value, label, detail]) => <button type="button" className={strategy === value ? "active" : ""} onClick={() => recalculate(value)} key={value}><b>{label}</b><small>{detail}</small></button>)}</div>
    </div>

    <div className="nifty-kpis">
      <article><small>EAN DANS VOTRE LGPI</small><strong>{sourceStats.matchedEanCount}</strong><span>sur {sourceStats.eligibleEanCount} EAN Nifty · {sourceStats.campaignCount} campagnes</span></article>
      <article><small>DÉCISIONS À PRENDRE</small><strong>{summary.toDecide}</strong><span>prix à valider ou appliquer</span></article>
      <article><small>FIN DANS 7 JOURS</small><strong>{summary.urgent}</strong><span>retours de prix à anticiper</span></article>
      <article><small>GAIN PATIENT SUR LE STOCK</small><strong>{money(summary.patientSaving)}</strong><span>tout en renforçant votre marge</span></article>
    </div>

    {summary.expired > 0 && <button type="button" className="nifty-expired-banner" onClick={() => setFilter("expired")}>
      <span>⚠️</span><b>{summary.expired} prix Nifty expiré{summary.expired > 1 ? "s" : ""} à remettre à jour</b><small>Le compagnon vous le rappellera chaque jour jusqu’au statut « Prix revenu ».</small><strong>Voir les produits →</strong>
    </button>}

    <section className="nifty-campaign-groups" aria-label="Groupes Nifty par laboratoire et date de fin">
      <header><div><small>TRAVAIL PAR GROUPE</small><h3>Laboratoires et familles détectés dans le fichier Nifty</h3></div><button type="button" className={groupFilter === "all" ? "active" : ""} onClick={() => setGroupFilter("all")}>Tous · {rows.length}</button></header>
      <div>{campaignGroups.map((group) => {
        const remaining = daysUntil(group.endDate);
        return <button type="button" key={group.key} className={`${groupFilter === group.key ? "active " : ""}${remaining < 0 ? "expired" : remaining <= 7 ? "urgent" : ""}`} onClick={() => setGroupFilter(groupFilter === group.key ? "all" : group.key)}>
          <span><b>{group.laboratory}</b><small>{[...group.families].join(" · ")}</small></span>
          <span><strong>{group.count}</strong><small>produit{group.count > 1 ? "s" : ""}</small></span>
          <span><b>{group.endDate ? dateLabel(group.endDate) : "À qualifier"}</b><small>{remaining < 0 ? "Terminée" : remaining <= 7 ? `J-${remaining}` : "Date de fin"}</small></span>
        </button>;
      })}</div>
    </section>

    <div className="nifty-list-tools">
      <label><span>Rechercher</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Produit, laboratoire, campagne ou EAN" /></label>
      <label><span>Groupe laboratoire</span><select value={groupFilter} onChange={(event) => setGroupFilter(event.target.value)}><option value="all">Tous les groupes</option>{campaignGroups.map((group) => <option value={group.key} key={group.key}>{group.laboratory} · fin {dateLabel(group.endDate)} · {group.count} produit{group.count > 1 ? "s" : ""}</option>)}</select></label>
      <label><span>Afficher</span><select value={filter} onChange={(event) => setFilter(event.target.value as typeof filter)}><option value="all">Tous les produits</option><option value="decided">Toutes les décisions enregistrées</option><option value="to_decide">Décision à prendre</option><option value="ready">Prix validés</option><option value="applied">Prix changés dans LGPI</option><option value="returned">Prix d’origine remis</option><option value="urgent">Fin dans 7 jours</option><option value="expired">Promotions terminées</option></select></label>
      <label><span>Classer par</span><select value={sort} onChange={(event) => setSort(event.target.value as typeof sort)}><option value="potential">Potentiel de marge</option><option value="supplier">Laboratoire</option><option value="end">Fin de campagne</option><option value="decision">Décision</option><option value="product">Produit</option></select></label>
      <button type="button" className="secondary" onClick={exportCsv} disabled={!displayedRows.length}>Exporter pour LGPI</button>
      <button type="button" onClick={() => void save()} disabled={saving || !selectedRows.length}>{saving ? "Traitement…" : `Enregistrer la sélection${selectedRows.length ? ` · ${selectedRows.length}` : ""}`}</button>
      <button type="button" className="danger" onClick={() => void deleteDecisions(displayedDecisionRows)} disabled={saving || !displayedDecisionRows.length}>🗑 Épurer les décisions affichées · {displayedDecisionRows.length}</button>
    </div>

    <div className={`nifty-bulk-bar${selectedRows.length ? " active" : ""}`}>
      <span><b>{selectedRows.length || "Aucun"} produit{selectedRows.length > 1 ? "s" : ""} coché{selectedRows.length > 1 ? "s" : ""}</b><small>Seules les lignes cochées seront enregistrées, supprimées ou imprimées.</small></span>
      <button type="button" className="secondary" onClick={toggleDisplayed} disabled={!displayedRows.length}>{allDisplayedSelected ? "Décocher les affichés" : "Cocher les affichés"}</button>
      <button type="button" className="danger" onClick={() => void deleteDecisions(selectedRows)} disabled={saving || !selectedRows.length}>🗑 Supprimer les décisions cochées</button>
      <button type="button" onClick={() => setPromoDialog(true)} disabled={!selectedRows.length}>🏷️ Créer les étiquettes promo</button>
    </div>

    {loading ? <div className="nifty-loading">Croisement des offres Nifty avec la base LGPI…</div> : displayedRows.length ? <div className="nifty-table"><table><thead><tr><th className="nifty-select-cell"><input type="checkbox" checked={allDisplayedSelected} onChange={toggleDisplayed} aria-label="Sélectionner tous les produits affichés" /></th><th>Produit concerné</th><th>Offre et période</th><th>Prix LGPI</th><th>Nouveau prix</th><th>Patient paie</th><th>Gain de marge</th><th>Après promo</th><th>Prix changé ?</th></tr></thead><tbody>{displayedRows.map((row) => {
      const value = economics(row);
      const remaining = daysUntil(row.campaign.endDate);
      return <tr id={`nifty-row-${row.ean}`} key={row.ean} className={remaining < 0 ? "expired" : remaining <= 7 ? "urgent" : ""}>
        <td className="nifty-select-cell"><input type="checkbox" checked={selectedEans.includes(row.ean)} onChange={() => toggleSelection(row.ean)} aria-label={`Sélectionner la décision pour ${row.product}`} /></td>
        <td><b>{row.product || row.campaign.title}</b><small>{row.ean} · stock {numberValue(row.stock).toLocaleString("fr-FR")}{row.campaign.laboratory ? ` · ${row.campaign.laboratory}` : row.supplier ? ` · ${row.supplier}` : ""}</small></td>
        <td><strong className="nifty-discount">{discountLabel(row)}</strong><small>{row.campaign.laboratory} · {row.campaign.title}<br />jusqu’au {dateLabel(row.campaign.endDate)}{remaining >= 0 && remaining <= 7 ? ` · J-${remaining}` : remaining < 0 ? " · TERMINÉE" : ""}</small></td>
        <td><b>{money(value.baseline)}</b><small>Achat HT {numberValue(row.purchase_price) ? money(numberValue(row.purchase_price)) : "non renseigné"}</small></td>
        <td><label className="nifty-price-input"><input type="number" min="0" step="0.05" value={row.proposed_price || ""} onChange={(event) => updateRow(row.ean, { proposed_price: numberValue(event.target.value) })} /><span>€ TTC</span></label><small>Taux de marque {value.markRate ? `${value.markRate.toFixed(1)}%` : "—"}</small></td>
        <td><b className={value.patientSaving > 0 ? "good" : "warning"}>{money(value.netPatient)}</b><small>{value.patientSaving > 0 ? `patient gagne ${money(value.patientSaving)}` : "aucun gain patient"}</small></td>
        <td><b className="good">+{money(value.marginGain)} HT</b><small>{numberValue(row.stock) ? `${money(value.stockGain)} sur le stock` : numberValue(row.monthly_sales) ? `${money(value.monthlyGain)} / mois estimé` : "par unité vendue"}</small></td>
        <td><label className="nifty-return-input"><input type="number" min="0" step="0.05" value={row.return_price || ""} onChange={(event) => updateRow(row.ean, { return_price: numberValue(event.target.value) })} /><span>€</span></label><small>à remettre le {dateLabel(row.campaign.endDate)}</small></td>
        <td><div className="nifty-decision-actions"><div className="nifty-changed-choice" aria-label={`Prix changé pour ${row.product}`}><button type="button" className={row.status !== "applied" ? "active no" : ""} onClick={() => updateRow(row.ean, { status: remaining < 0 ? "returned" : "ready" })}>Non</button><button type="button" className={row.status === "applied" ? "active yes" : ""} onClick={() => updateRow(row.ean, { status: "applied" })}>Oui</button></div>{row.status !== "to_decide" && <button type="button" className="nifty-row-delete" onClick={() => void deleteDecisions([row])} disabled={saving} aria-label={`Supprimer la décision pour ${row.product}`} title="Supprimer cette décision">🗑</button>}</div><small>{row.status === "applied" ? "Modifié dans LGPI" : row.status === "returned" ? "Prix d’origine remis" : "Pas encore modifié"}</small></td>
      </tr>;
    })}</tbody></table></div> : <div className="nifty-loading">Aucun produit du catalogue {pharmacy} ne correspond actuellement aux EAN Nifty.</div>}

    <section className="nifty-history">
      <header><div><small>JOURNAL DES DÉCISIONS · {pharmacy.toUpperCase()}</small><h3>Historique par campagne Nifty</h3><p>Chaque laboratoire et période forme un dossier repliable. Ouvrez-le pour retrouver et corriger les décisions produit par produit.</p></div><strong>{historyCampaigns.length} campagne{historyCampaigns.length > 1 ? "s" : ""}</strong></header>
      {historyCampaigns.length ? <div className="nifty-history-list">{historyCampaigns.map((campaign) => {
        const changedCount = campaign.changes.filter((change) => change.action !== "deleted" && change.priceChanged).length;
        const deletedCount = campaign.changes.filter((change) => change.action === "deleted").length;
        return <details key={campaign.key}>
        <summary><span><time>Nifty · {campaign.laboratory}</time>{campaign.campaignTitle && campaign.campaignTitle !== campaign.laboratory && <strong>{campaign.campaignTitle}</strong>}<small>{campaign.startDate || campaign.endDate ? `Période du ${dateLabel(campaign.startDate)} au ${dateLabel(campaign.endDate)}` : "Période non renseignée"} · dernière décision le {new Date(campaign.updatedAt).toLocaleDateString("fr-FR")}</small></span><b>{campaign.changes.length} décision{campaign.changes.length > 1 ? "s" : ""} · {changedCount} prix changé{changedCount > 1 ? "s" : ""}{deletedCount ? ` · ${deletedCount} supprimée${deletedCount > 1 ? "s" : ""}` : ""}</b></summary>
        <div>{campaign.changes.map((change) => <div className="nifty-history-change" key={`${campaign.key}-${change.ean}`}>
          <span><b>{change.product || change.ean}</b><small>{new Date(change.recordedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} · EAN {change.ean} · {change.laboratory || change.campaignTitle}</small></span>
          <span><small>Prix mémorisé</small><b>{money(change.previousPrice)} → {money(change.proposedPrice)}</b></span>
          <span><small>Net Nifty</small><b>{money(change.netPatient)}</b></span>
          <span><small>À remettre après</small><b>{money(change.returnPrice)}</b></span>
          <span className={change.priceChanged ? "changed" : "unchanged"}><small>{change.action === "deleted" ? "Décision" : "Prix changé"}</small><b>{change.action === "deleted" ? "Supprimée" : change.priceChanged ? "Oui" : "Non"}</b></span>
          <nav><button type="button" onClick={() => editHistoryDecision(change)} disabled={!rows.some((row) => row.ean === change.ean)}>✎ Modifier</button><button type="button" className="delete" onClick={() => deleteHistoryDecision(change)} disabled={!rows.some((row) => row.ean === change.ean && row.status !== "to_decide")}>🗑 Supprimer</button></nav>
        </div>)}</div>
      </details>})}</div> : <div className="nifty-history-empty">Aucun historique enregistré pour {pharmacy}. Le premier apparaîtra après « Enregistrer la sélection ».</div>}
    </section>

    <footer className="nifty-caution"><b>Principe de calcul</b><span>Le patient paie le prix affiché moins la réduction. Votre recette théorique reste le prix affiché si Nifty rembourse bien la remise. Le gain de marge est donc calculé sur ce prix, à contrôler avec le suivi des règlements Nifty. Aucun prix n’est modifié automatiquement dans LGPI.</span></footer>
    {notice && <div className="module-toast" role="status" onClick={() => setNotice("")}>{notice}</div>}
    {promoDialog && <div className="nifty-promo-overlay" role="dialog" aria-modal="true" aria-label="Choisir le modèle des étiquettes promo">
      <section className="nifty-promo-dialog">
        <header><div><small>IMPRESSION GROUPÉE</small><h3>Choisir le modèle d’étiquette</h3><p>{selectedRows.length} produit{selectedRows.length > 1 ? "s" : ""} sélectionné{selectedRows.length > 1 ? "s" : ""} · seuls les produits cochés seront imprimés.</p></div><button type="button" onClick={() => setPromoDialog(false)} aria-label="Fermer">×</button></header>
        <div className="nifty-promo-dialog-body">
          <fieldset><legend>Dimension</legend><div className="nifty-promo-options">{promoFormats.map((format) => <button type="button" className={promoFormat === format.value ? "active" : ""} onClick={() => setPromoFormat(format.value)} key={format.value}><b>{format.label}</b><small>{format.size}</small></button>)}</div></fieldset>
          <fieldset><legend>Ambiance</legend><div className="nifty-promo-styles">{promoStyles.map((style) => <button type="button" className={`${style.value}${promoStyle === style.value ? " active" : ""}`} onClick={() => setPromoStyle(style.value)} key={style.value}>{style.label}</button>)}</div></fieldset>
          <label className="nifty-promo-copies"><span>Nombre d’exemplaires par produit</span><input type="number" min="1" max="20" value={promoCopies} onChange={(event) => setPromoCopies(Math.max(1, Math.min(20, numberValue(event.target.value) || 1)))} /></label>
          {selectedRows[0] && <div className={`nifty-label-preview ${promoStyle}`}><div className="nifty-label-top"><small>{pharmacy.toUpperCase()} · PROMO NIFTY</small><em>ÉCONOMISEZ {money(Math.max(0, economics(selectedRows[0]).patientSaving))}</em></div><b>{selectedRows[0].product}</b><div className="nifty-preview-prices"><span><small>PRIX AVANT REMISE</small><s>{money(economics(selectedRows[0]).proposed)}</s></span><span><small>APRÈS REMISE NIFTY</small><strong>{money(economics(selectedRows[0]).netPatient)}</strong></span></div><span>{discountLabel(selectedRows[0])} calculé sur {money(economics(selectedRows[0]).proposed)} · EAN {selectedRows[0].ean}</span></div>}
        </div>
        <footer><button type="button" className="secondary" onClick={() => setPromoDialog(false)}>Annuler</button><button type="button" onClick={printPromoLabels}>Aperçu avant impression · {selectedRows.length * promoCopies} étiquette{selectedRows.length * promoCopies > 1 ? "s" : ""}</button></footer>
      </section>
    </div>}
  </section>
  {typeof document !== "undefined" ? createPortal(<section className={`nifty-promo-print ${promoFormat}`} aria-hidden="true">
    {selectedRows.flatMap((row) => Array.from({ length: promoCopies }, (_, index) => {
      const value = economics(row);
      return <article className={`nifty-bulk-label ${promoStyle}`} key={`${row.ean}-${index}`}>
        <div className="nifty-label-top"><small>{pharmacy.toUpperCase()} · PROMO NIFTY</small><em>ÉCONOMISEZ {money(Math.max(0, value.patientSaving))}</em></div><h2>{row.product || row.campaign.title}</h2><div className="nifty-bulk-prices"><span className="before"><small>PRIX AVANT REMISE</small><s>{money(value.proposed)}</s></span><span className="after"><small>APRÈS REMISE NIFTY</small><strong>{money(value.netPatient)}</strong></span></div><p>{discountLabel(row)} calculé sur {money(value.proposed)} · jusqu’au {dateLabel(row.campaign.endDate)}</p><footer><b>EAN {row.ean}</b></footer>
      </article>;
    }))}
  </section>, document.body) : null}</>;
}
